<!-- template for the modal component -->
<template type="x/template">
  <transition name="modal" id="contact-info-dialog">
    <div class="modal-mask" v-show="show">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3>{{ title }}</h3>
          <button type="button" class="btn-close dialog-close-button" :aria-label="$t('common.close')" @click="close" />
        </div>
        <div class="modal-body">
          <div class="row">
            <div class="col-lg-12">
              <label class="control-label">
                <h5 class="role-name-label">{{ $t("role_name") }}</h5>
              </label>
              <input type="text" v-model="modalData.name" :class="{ 'field-error': this.$v.modalData.name.$error }" class="form-control" />
            </div>
          </div>
          <div class="row pt-3">
            <div class="col-lg-12">
              <h5>{{ $t("permissions") }}</h5>
              <hr />
            </div>
            <div class="col-lg-12">
              <DxDataGrid
                ref="permissionsGrid"
                :data-source="availablePermissions"
                key-expr="uuid"
                :show-borders="true"
                :show-column-headers="true"
                :row-alternation-enabled="true"
                :hover-state-enabled="true"
                height="60vh"
                @content-ready="onGridContentReady"
              >
                <DxPaging :page-size="50" />
                <DxPager :visible="false" />
                <DxScrolling mode="infinite" />
                <DxFilterRow :visible="true" :show-operation-chooser="false" />
                <DxColumn
                  data-field="selected"
                  caption=" "
                  :width="48"
                  :allow-filtering="false"
                  :allow-sorting="false"
                  data-type="boolean"
                  true-text="Yes"
                  false-text="No"
                  cell-template="moduleCheckboxCell"
                  header-cell-template="selectAllCheckboxHeader"
                />
                <DxColumn data-field="name" :caption="$t('common.name')" :allow-editing="false" />
                <DxColumn
                  data-field="access.r"
                  :caption="$t('common.read')"
                  data-type="boolean"
                  true-text="Yes"
                  false-text="No"
                  cell-template="readCheckboxCell"
                />
                <DxColumn
                  :caption="$t('common.create') + '/' + $t('common.update')"
                  data-field="access.w"
                  data-type="boolean"
                  true-text="Yes"
                  false-text="No"
                  cell-template="writeCheckboxCell"
                />
                <DxColumn
                  data-field="access.x"
                  :caption="$t('common.delete')"
                  data-type="boolean"
                  true-text="Yes"
                  false-text="No"
                  cell-template="deleteCheckboxCell"
                />
                <template #selectAllCheckboxHeader>
                  <div class="d-flex align-items-center justify-content-center">
                    <input
                      type="checkbox"
                      :checked="allModulesSelected"
                      :indeterminate.prop="someModulesSelected && !allModulesSelected"
                      @click.stop
                      @change.stop="toggleAllModules($event.target.checked)"
                    />
                  </div>
                </template>
                <template #moduleCheckboxCell="{ data }">
                  <input type="checkbox" :checked="!!data.data.selected" @change="toggleModule(data.data, $event.target.checked)" />
                </template>
                <template #readCheckboxCell="{ data }">
                  <input type="checkbox" :checked="!!data.data.access.r" @change="toggleAccess(data.data, 'r', $event.target.checked)" />
                </template>
                <template #writeCheckboxCell="{ data }">
                  <input type="checkbox" :checked="!!data.data.access.w" @change="toggleAccess(data.data, 'w', $event.target.checked)" />
                </template>
                <template #deleteCheckboxCell="{ data }">
                  <input type="checkbox" :checked="!!data.data.access.x" @change="toggleAccess(data.data, 'x', $event.target.checked)" />
                </template>
              </DxDataGrid>
            </div>
          </div>
        </div>
        <div class="modal-footer col-lg-12">
          <div class="form-group d-flex justify-content-center gap-3 w-100">
            <SmagriaButton :label="$t('common.save')" :action="OK" :customClass="'primary'" />
            <SmagriaButton :label="$t('common.close')" :action="close" :customClass="'reset border'" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import DxDataGrid, { DxColumn, DxFilterRow, DxPaging, DxPager, DxScrolling } from "devextreme-vue/data-grid";

export default {
  name: "EditRole",
  props: ["show", "data"],
  data() {
    return {
      modalData: this.data ? this.data : {},
      error: "",
      visiblePermissionRows: [],
    };
  },
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxPaging,
    DxPager,
    DxScrolling,
    SmagriaButton,
  },
  computed: {
    ...mapGetters({
      permissions: "roles/permissions",
    }),
    availablePermissions() {
      return this.modalData.available_permissions || [];
    },
    allModulesSelected() {
      return this.visiblePermissionRows.length > 0 && this.visiblePermissionRows.every((item) => item.selected);
    },
    someModulesSelected() {
      return this.visiblePermissionRows.some((item) => item.selected);
    },
    title() {
      return this.modalData.uuid ? this.$t("edit_role") : this.$t("new_role");
    },
  },
  methods: {
    close: function () {
      const grid = this.$refs.permissionsGrid;
      if (grid && grid.instance) {
        grid.instance.clearFilter();
      }
      this.visiblePermissionRows = [];
      this.$emit("close");
    },
    OK() {
      this.$v.modalData.$touch();

      if (!this.$v.modalData.$invalid) {
        this.modalData.permissions = this.buildPermissions();
        this.$emit("onSaved", this.modalData);
        this.close();
      } else {
        this.setErrorMessage(this.$t("fill_out_error"));
      }
    },
    saveAsTemplate() {
      this.$emit("onSaveAsTemplate", this.modalData);
    },
    setErrorMessage(msg) {
      this.error = msg;
    },
    buildPermissions() {
      if (this.modalData && this.modalData.available_permissions) {
        let permissions = [];

        Object.assign([], this.modalData.available_permissions).map((item) => {
          if (item.selected) {
            permissions.push({
              uuid: item.uuid,
              access: Object.keys(item.access).filter((key) => {
                if (item.access[key] && key.length === 1) {
                  return key;
                }
              }),
              code: item.code,
              name: item.name,
            });
          }
        });

        return permissions;
      }
    },
    hasPermission(item) {
      if (this.modalData.permissions) {
        return this.modalData.permissions.find((p) => p.code === item.code);
      }
    },
    checkPermissions() {
      let vm = this;
      if (this.modalData.available_permissions) {
        this.modalData.available_permissions.map((item) => {
          if (vm.hasPermission(item)) {
            item.selected = true;

            let perm = vm.getPermission(item);

            if (perm && perm.access) {
              item.access = perm.access;
            } else {
              return { r: false, w: false, x: false };
            }
          } else {
            item.access = { r: false, w: false, x: false };
            item.selected = false;
            item.access.disable = true;
            //original inherited access
          }
        });
      }
    },
    getPermission(permission_item) {
      if (this.modalData.permissions) {
        let found = this.modalData.permissions.find((p) => p.code == permission_item.code);

        if (found && found.access) {
          return found;
        }
      }
    },
    loadPermissions() {
      this.$store.dispatch("roles/getDefaultPermissions", {});
    },
    toggleAllModules(value) {
      this.visiblePermissionRows.forEach((item) => this.toggleModule(item, value));
    },
    onGridContentReady(e) {
      this.visiblePermissionRows = e.component
        .getVisibleRows()
        .filter((row) => row.rowType === "data")
        .map((row) => row.data);
    },
    toggleModule(item, value) {
      this.$set(item, "selected", value);
      this.$set(item, "access", Object.assign({}, item.access, { r: value, w: value, x: value }));
    },
    toggleAccess(item, permission, value) {
      this.$set(item.access, permission, value);
      if (value) {
        this.$set(item, "selected", true);
      }
    },
  },
  watch: {
    data: function (newVal) {
      this.modalData = newVal;
      this.checkPermissions();
    },
    show: function () {
      this.setErrorMessage("");
    },
  },
  validations() {
    return {
      modalData: {
        name: {
          required,
        },
      },
    };
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.modal-mask {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.modal-container {
  width: 70% !important;
  max-height: 90vh;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding-bottom: 8px;
}

.modal-header h3 {
  margin: 0;
}

.role-name-label {
  margin-bottom: 0;
  padding: 0;
}

.dialog-close-button {
  flex: 0 0 auto;
  margin-left: auto;
}

.modal-body {
  flex: 1;
  overflow-y: hidden;
  overflow-x: hidden;
  margin: 0;
}

.modal-footer {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-footer .form-group {
  margin-bottom: 0;
}
</style>
